import { useCallback, useRef } from 'react'

export interface SecretGestureOptions {
  /** Quantos taps/clicks consecutivos devem ocorrer dentro da janela. */
  count: number
  /** Janela de tempo (ms) em que os taps precisam acontecer para contar. */
  windowMs: number
  /** Disparado quando o threshold é atingido. */
  onTrigger: () => void
}

/**
 * Detecta uma sequência rápida de cliques/taps em um elemento.
 *
 * Use para esconder atalhos administrativos atrás de um gesto não-óbvio
 * (ex.: 5 cliques no logo da tela de login em < 2s). Funciona em desktop
 * (click) e mobile (tap), pois o navegador converte tap em click.
 *
 * O array de timestamps é mantido em ref para não causar re-renders;
 * entradas antigas (fora da janela) são descartadas a cada novo tap.
 */
export function useSecretGesture(
  opts: SecretGestureOptions
): React.MouseEventHandler {
  const tapsRef = useRef<number[]>([])
  const { count, windowMs, onTrigger } = opts

  return useCallback(() => {
    const now = Date.now()
    tapsRef.current = [...tapsRef.current, now].filter(t => now - t < windowMs)
    if (tapsRef.current.length >= count) {
      tapsRef.current = []
      onTrigger()
    }
  }, [count, windowMs, onTrigger])
}
